import AbstractShape from "@/Models/Shapes/AbstractShape";
import { PreviewShapeFactory } from "@/Models/Shapes/PreviewShapeFactory";
import ShapeConfigurationFactory from "@/Models/ShapeConfigurations/ShapeConfigurationFactory";
import { convertUnits } from "@/functions/numbers";
import isEmpty from "lodash/isEmpty";
import omitBy from "lodash/omitBy";

export default {
	namespaced: true,
	getters: {
		getInstance: ( state, getters, rootState, { measurements } ): AbstractShape => {
			return ( new PreviewShapeFactory() ).get( {
				shape: 'rounded-rectangle',
				dimensions: measurements.dimensions,
				modifiers: measurements.modifiers,
			} );
		},
		cutoutPrimitives: ( state, getters, rootState, { cutoutMeasurements } ): object => {
			return convertUnits( cutoutMeasurements.dimensions[ 'rounded-rectangle' ] );
		},
		getCutoutArea: ( state, getters, rootState, { cutoutMeasurements, getArea } ): number => {
			return getArea( 'rounded-rectangle', cutoutMeasurements.dimensions[ 'rounded-rectangle' ] );
		},
		isCenteredShape: (): boolean => false,
		getArea: ( state, getters, rootState, { getArea, parsedDimensions } ): object => {
			return getArea( 'rounded-rectangle', parsedDimensions );
		},
		useIndividualCorners: ( state, getters, rootState ): boolean => {
			const { pricingGroup } = window.measurementSelectorVariables;
			const { modifiers } = rootState.measurements;

			if ( 'use_individual_corners' in modifiers ) {
				return modifiers.use_individual_corners === true;
			}

			if ( ! ( 'rounded-rectangle' in pricingGroup.shape_settings ) ) {
				return false;
			}

			return pricingGroup.shape_settings[ 'rounded-rectangle' ][ 'use_individual_corners' ];
		},
		defaultCutoutDimensions: ( state, getters, rootState, rootGetters ): Record<string, number> => {
			const parent = new PreviewShapeFactory().get( { shape: rootGetters.shape } ).getDimensionProperties();
			const { primary, secondary } = parent;

			const dimensions = {
				width: rootGetters.dimensions[primary] / 2,
				length: rootGetters.dimensions[secondary ?? primary] / 2,
			};

			if ( getters.useIndividualCorners ) {
				dimensions['top_left_radius'] = rootGetters.dimensions[primary] / 10;
				dimensions['top_right_radius'] = rootGetters.dimensions[primary] / 10;
				dimensions['bottom_left_radius'] = rootGetters.dimensions[primary] / 10;
				dimensions['bottom_right_radius'] = rootGetters.dimensions[primary] / 10;
			} else {
				dimensions['radius'] = rootGetters.dimensions[primary] / 10;
			}

			return dimensions;
		},
		dimensionPreview: ( state, getters, rootState, rootGetters ): string => {

			// Filter out dimension keys that don't exist within the shape.
			const configuration = ( new ShapeConfigurationFactory() ).get_configuration( 'rounded-rectangle' );
			const dimensions = omitBy( rootGetters.parsedDimensions, ( dimension: unknown, key: string ): boolean => {
				return configuration.get_dimension_keys().indexOf( key ) === -1;
			} );

			const commonDimensions = [];
			const radiusDimensions = [];

			for ( const [key, value] of Object.entries( dimensions ) ) {
				if ( key.indexOf( 'radius' ) !== -1 ) {
					radiusDimensions.push( value );

					continue;
				}

				commonDimensions.push( value );
			}

			const { radius, radii } = window.measurementSelectorVariables.i18n.validators.dimensions.labels;

			const label = radiusDimensions.length === 1 ? radius : radii;
			const dimensionLabels = commonDimensions.map( value => value + 'cm' ).join( ' × ' );
			const radiusLabels = radiusDimensions.map( value => value + 'cm' ).join( '/' );

			return `${dimensionLabels} ${label}: ${radiusLabels}`;
		}
	},
};
